import { thunkProgress } from 'redux-progress';

import type { Dispatch, GetState, State } from 'model/State';
import {
  childrenOverviews,
  isSiteApplicableForConcession,
  isSiteOnConcession,
  CMTSelfServeLink,
  isOnBehalfOfEmail,
  getAccountHistory,
  trackedElsewhere,
  archiveUnarchiveAccount,
  getPricingPlanAccountOverview,
  cancelSubscription,
  sendChurnSurvey,
  overviewWithOrgChildren,
  isHostedAccountProvisioning,
  updateServerId,
  getCreditCardUpdatableAccounts,
  getRenewalTotalForDisplay,
  getCustomerAccountOverviewsByPartnerSearchDTO,
  isSuspended,
  postCreditCardDetails,
  postUpdateRenewalCurrencyOnReconcilableValidationResultAccounts,
  postActivate,
  createPersistedLicense,
  cancelAutoRenew,
  editLicenseName,
  getAccountHistoryAsPartner,
} from 'modules/mac/duck/api';
import {
  CHILDREN_OVERVIEWS,
  IS_SITE_APPLICABLE_FOR_CONCESSION,
  CMT_SELF_SERVE_LINK,
  IS_ON_BEHALF_OF_EMAIL,
  ACCOUNT_HISTORY,
  ACCOUNT_HISTORY_AS_PARTNER,
  TRACKED_ELSEWHERE,
  ARCHIVE_UNARCHIVE_ACCOUNT,
  IS_SITE_ON_CONCESSION,
  PRICING_PLAN_OVERVIEW,
  CANCEL_SUBSCRIPTION,
  COMPLETE_CHURN_SURVEY,
  OVERVIEW_WITH_ORG_CHILDREN,
  UPDATE_SID,
  IS_HOSTED_ACCOUNT_PROVISIONING,
  CREDIT_CARD_UPDATABLE_ACCOUNTS,
  RENEWAL_TOTAL_FOR_DISPLAY,
  IS_SUSPENDED,
  POST_CREDIT_CARD_DETAILS,
  POST_CURRENCY_ON_RECONCILABLE_VALIDATION,
  POST_ACTIVATE_SUSPENDED,
  CUSTOMER_ACCOUNT_OVERVIEWS,
  CREATE_PERSISTED_LICENSE,
  CANCEL_AUTO_RENEW,
  EDIT_LICENSE_NAME_INLINE,
  RESTORE_EDIT_LICENSE_NAME_INLINE,
} from './types';
import { CustomerAccountOverviewSearchDTO } from '../../models';
import { PRODUCTION, STAGING } from '../../../../../model/EnvironmentConfig';
import { getEnvironment } from '../../../../../appconfig/environment';
import { CreateLicenseDetails } from '../../models/product';
import { LICENSE_EVALUATION } from '../../../../../model/hams/LicenseType';
import { getCurrentMACContactEmail } from '../../selectors';

export const selectAccountId = (state: State) =>
  state?.location?.query?.accountId ?? state?.location?.query?.partnerAccountId;

export const fetchChildrenOverviews = (accountId) => (dispatch: Dispatch, getState: GetState) => {
  const state = getState();

  const childrenOverviewsData = childrenOverviews(accountId, state);
  dispatch(thunkProgress(CHILDREN_OVERVIEWS, childrenOverviewsData));
};

export const fetchOverviewWithOrgChildren = () => (dispatch: Dispatch, getState: GetState) => {
  const state = getState();

  const overviewWithOrgChildrenData = overviewWithOrgChildren(state);
  dispatch(thunkProgress(OVERVIEW_WITH_ORG_CHILDREN, overviewWithOrgChildrenData));
};

export const fetchIsSiteApplicableForConcession = () => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  const state = getState();
  const accountId = selectAccountId(state);

  if (!accountId) return;

  const isSiteApplicableForConcessionData = isSiteApplicableForConcession(accountId, state);
  dispatch(thunkProgress(IS_SITE_APPLICABLE_FOR_CONCESSION, isSiteApplicableForConcessionData));
};

export const fetchTrackedElsewhere = (accountId: string, ufoName: string = '') => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  const state = getState();

  if (!accountId) return;

  const trackedElsewhereData = trackedElsewhere(accountId, state, ufoName);
  dispatch(thunkProgress(TRACKED_ELSEWHERE, trackedElsewhereData, { accountId }));
};

export const fetchIsSiteOnConcession = (accountId: number, ufoName: string = '') => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  const state = getState();

  const isSiteOnConcessionData = isSiteOnConcession(accountId, state, ufoName);
  dispatch(thunkProgress(IS_SITE_ON_CONCESSION, isSiteOnConcessionData, { accountId }));
};

export const fetchCMTSelfServeLink = () => (dispatch: Dispatch, getState: GetState) => {
  const state = getState();
  const accountId = selectAccountId(state);

  if (!accountId) return;

  const cmtSelfServeLinkData = CMTSelfServeLink(accountId, state);
  dispatch(thunkProgress(CMT_SELF_SERVE_LINK, cmtSelfServeLinkData));
};

export const fetchIsOnBehalfOfEmail = (accountId: string, ufoName: string = '') => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  const state = getState();

  if (!accountId) return;

  const isOnBehalfOfEmailData = isOnBehalfOfEmail(accountId, state, ufoName);
  dispatch(thunkProgress(IS_ON_BEHALF_OF_EMAIL, isOnBehalfOfEmailData));
};

export const fetchAccountHistory = (accountId: string, offset: number, maxResults: number) => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  const state = getState();

  if (!accountId) return;

  const accountHistory = getAccountHistory(accountId, offset, maxResults, state);
  dispatch(thunkProgress(ACCOUNT_HISTORY, accountHistory, { accountId }));
};

export const fetchAccountHistoryAsPartner = (
  accountId: string,
  offset: number,
  maxResults: number,
  includeZeroOrders: boolean = true,
) => (dispatch: Dispatch, getState: GetState) => {
  const state = getState();

  if (!accountId) return;

  const accountHistoryAsPartner = getAccountHistoryAsPartner(
    accountId,
    offset,
    maxResults,
    includeZeroOrders,
    state,
  );
  dispatch(thunkProgress(ACCOUNT_HISTORY_AS_PARTNER, accountHistoryAsPartner, { accountId }));
};

export const postArchiveUnarchiveAccount = (accountId: number, isArchived: boolean) => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  const state = getState();

  if (!accountId) return;

  const archiveUnarchiveAccountData = archiveUnarchiveAccount(
    accountId,
    isArchived || false,
    state,
  );
  dispatch(thunkProgress(ARCHIVE_UNARCHIVE_ACCOUNT, archiveUnarchiveAccountData));
};

export const fetchPricingPlanAccountOverview = (accountId: string) => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  const state = getState();

  if (!accountId) return;

  const pricingPlanAccountOverview = getPricingPlanAccountOverview(accountId, state);
  dispatch(thunkProgress(PRICING_PLAN_OVERVIEW, pricingPlanAccountOverview, { accountId }));
};

export const cancelCurrentSubscription = (
  accountId: string,
  openSurveyModal: () => void,
  closeModal: () => void,
) => (dispatch: Dispatch, getState: GetState) => {
  if (!accountId) return;
  dispatch(
    thunkProgress(
      CANCEL_SUBSCRIPTION,
      cancelSubscription(accountId, getState()).then(() => {
        closeModal();
        openSurveyModal();
      }),
    ),
  );
};

export const completeChurnSurvey = (
  accountId: string,
  message: string,
  messagekey: string,
  comment: string,
  closeModal: () => void,
) => (dispatch: Dispatch, getState: GetState) => {
  if (!accountId) return;

  const environmentMappings = {
    [STAGING]: 'stg',
    [PRODUCTION]: 'prod',
  };
  let env = getEnvironment().toString();
  env = environmentMappings[env] || env;

  dispatch(
    thunkProgress(
      COMPLETE_CHURN_SURVEY,
      sendChurnSurvey(accountId, message, messagekey, comment, env, getState()).then(() => {
        closeModal();
      }),
    ),
  );
};

export const updateServerIdAction = (accountId: string, sid: string) => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  const state = getState();

  if (!accountId) return;

  const updateSid = updateServerId(accountId, sid, state);
  dispatch(thunkProgress(UPDATE_SID, updateSid));
};

export const fetchIsHostedAccountProvisioning = (accountId: number, ufoName: string = '') => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  dispatch(
    thunkProgress(
      IS_HOSTED_ACCOUNT_PROVISIONING,
      isHostedAccountProvisioning(accountId, getState(), ufoName),
    ),
  );
};

export const fetchIsSuspended = (accountId: number) => (dispatch: Dispatch, getState: GetState) => {
  dispatch(thunkProgress(IS_SUSPENDED, isSuspended(accountId, getState())));
};

export const postActivateSuspended = (accountId: number) => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  dispatch(thunkProgress(POST_ACTIVATE_SUSPENDED, postActivate(accountId, getState())));
};

export const postCCDetails = (accountId: number) => (dispatch: Dispatch, getState: GetState) => {
  dispatch(thunkProgress(POST_CREDIT_CARD_DETAILS, postCreditCardDetails(accountId, getState())));
};

export const postReconcilableValidationResultAccounts = (validateCountryData: any) => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  dispatch(
    thunkProgress(
      POST_CURRENCY_ON_RECONCILABLE_VALIDATION,
      postUpdateRenewalCurrencyOnReconcilableValidationResultAccounts(
        validateCountryData,
        getState(),
      ),
    ),
  );
};

export const fetchCreditCardUpdatableAccounts = () => (dispatch: Dispatch, getState: GetState) => {
  const state = getState();

  const creditCardUpdatableAccountsData = getCreditCardUpdatableAccounts(state);
  dispatch(thunkProgress(CREDIT_CARD_UPDATABLE_ACCOUNTS, creditCardUpdatableAccountsData));
};

export const fetchRenewalTotal = (
  accountId: string,
  shouldCalculateTax: boolean,
  displayCents: boolean,
  displayTaxType: boolean,
  displayPaymentPeriod: boolean,
) => (dispatch: Dispatch, getState: GetState) => {
  const state = getState();

  if (!accountId) return;

  const renewalTotal = getRenewalTotalForDisplay(
    accountId,
    shouldCalculateTax,
    displayCents,
    displayTaxType,
    displayPaymentPeriod,
    state,
  );
  dispatch(thunkProgress(RENEWAL_TOTAL_FOR_DISPLAY, renewalTotal, { accountId }));
};

export const fetchCustomerAccountOverviews = (
  searchObject: CustomerAccountOverviewSearchDTO,
  max: number,
  offset: number,
  total: boolean,
) => (dispatch: Dispatch, getState: GetState) =>
  dispatch(
    thunkProgress(
      CUSTOMER_ACCOUNT_OVERVIEWS,
      getCustomerAccountOverviewsByPartnerSearchDTO(searchObject, getState(), max, offset, total),
    ),
  );

export const createPersistedLicenseAction = (
  productKey: string,
  organisationName: string,
  serverId?: string | null,
) => (dispatch: Dispatch, getState: GetState) => {
  const userEmail = getCurrentMACContactEmail(getState()) || '';

  const licenseDetails: CreateLicenseDetails = {
    productKey,
    email: userEmail,
    serverId: serverId || '',
    organisationName,
    licenseType: LICENSE_EVALUATION,
    numberOfUsers: 987654321,
    utmSource: 'MAC',
  };

  dispatch(
    thunkProgress(CREATE_PERSISTED_LICENSE, createPersistedLicense(licenseDetails, getState())),
  );
};

export const cancelLicenseAutoRenew = (accountId: number) => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  dispatch(thunkProgress(CANCEL_AUTO_RENEW, cancelAutoRenew(accountId, getState()), { accountId }));
};

export const editLicenseNameInline = (accountId: number, newName: string) => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  dispatch(
    thunkProgress(EDIT_LICENSE_NAME_INLINE, editLicenseName(accountId, newName, getState())),
  );
};

export const restoreEditLicenseNameInline = () => (dispatch: Dispatch) => {
  dispatch({ type: RESTORE_EDIT_LICENSE_NAME_INLINE });
};
